import React, { useState, useEffect, useRef, useMemo } from 'react';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { getWarehouses } from '../../services/warehousesService';
import { authService } from '../../services/authService';
import { filterWarehousesForUser, getLimitedUserScope } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const PAGE_SIZE = 50;

const conditionLabels = {
  good: 'Bueno',
  damaged: 'Dañado',
  defective: 'Defectuoso',
  reusable: 'Reutilizable',
  non_reusable: 'No reutilizable',
};

const getConditionLabel = (row) =>
  row?.condition_label || conditionLabels[row?.condition] || row?.condition || '-';

const kardexEventLabels = {
  compra: 'Compra',
  venta: 'Venta',
  saldo_inicial: 'Saldo inicial',
  traspaso: 'Traspaso',
  recepcion: 'Recepción',
  devolucion: 'Devolución',
  transferencia_gratuita: 'Transferencia gratuita',
  descuento_obtenido: 'Descuento obtenido',
  descuento_concedido: 'Descuento concedido',
  descuento_con_mercaderia: 'Descuento con mercadería',
  consumo: 'Consumo',
  ajuste_inventario: 'Ajuste de inventario',
  ajuste_costo: 'Ajuste de costo',
  salida_produccion: 'Salida a producción',
  entrada_produccion: 'Entrada de producción',
  entrada_producto_terminado: 'Entrada producto terminado',
};

const getKardexEventLabel = (row) =>
  row?.kardex_event_label || kardexEventLabels[row?.kardex_event] || row?.kardex_event || '-';

const EntriesExitsMatrix = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [warehouses, setWarehouses] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedWarehouses = useRef(false);

  const [monthFrom, setMonthFrom] = useState(currentMonth);
  const [yearFrom, setYearFrom] = useState(currentYear);
  const [monthTo, setMonthTo] = useState(currentMonth);
  const [yearTo, setYearTo] = useState(currentYear);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  useEffect(() => {
    if (!hasLoadedWarehouses.current) {
      hasLoadedWarehouses.current = true;
      loadWarehouses();
    }
  }, []);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses({ active_only: true });
      if (response.success) {
        const user = authService.getCurrentUser();
        const filtered = filterWarehousesForUser(response.data || [], user);
        setWarehouses(filtered);
        if (filtered.length === 1) {
          setSelectedWarehouse(String(filtered[0].id));
        }
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar almacenes', type: 'error' });
    }
  };

  const validatePeriod = () => {
    if (!monthFrom || !yearFrom || !monthTo || !yearTo) {
      setToast({ isOpen: true, message: 'Indique mes y año desde y hasta', type: 'error' });
      return false;
    }
    if (yearFrom > yearTo || (yearFrom === yearTo && monthFrom > monthTo)) {
      setToast({
        isOpen: true,
        message: 'El mes desde debe ser anterior o igual al mes hasta',
        type: 'error',
      });
      return false;
    }
    return true;
  };

  const loadReport = async () => {
    if (!validatePeriod()) return;

    try {
      setLoading(true);
      const response = await reportService.getEntriesExitsMatrixReport({
        monthFrom,
        yearFrom,
        monthTo,
        yearTo,
        warehouseId: selectedWarehouse || null,
      });

      if (response.success) {
        setReportData(response.data);
        setCurrentPage(1);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el reporte',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar la matriz de entradas y salidas',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(`${value}T12:00:00`);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const exportToExcel = async () => {
    if (!reportData) return;

    try {
      const workbook = new ExcelJS.Workbook();

      const headerStyle = {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
        border: {
          top: { style: 'thin' }, bottom: { style: 'thin' },
          left: { style: 'thin' }, right: { style: 'thin' },
        },
      };

      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E78' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7F3FF' } },
      };

      const warehouseLabel = selectedWarehouse
        ? (warehouses.find((w) => String(w.id) === String(selectedWarehouse))?.name || 'Almacén')
        : allWarehousesLabel;

      // Hoja Guías
      const wsGuides = workbook.addWorksheet('Guías');
      wsGuides.mergeCells('A1:L1');
      wsGuides.getCell('A1').value = `Matriz Entradas y Salidas — ${reportData.period_label}`;
      wsGuides.getCell('A1').style = titleStyle;
      wsGuides.mergeCells('A2:L2');
      wsGuides.getCell('A2').value = `Almacén: ${warehouseLabel} | Generado: ${new Date().toLocaleString('es-PE')}`;
      wsGuides.getCell('A2').alignment = { horizontal: 'center' };

      const guideHeaders = [
        'Código', 'Tipo', 'F. Emisión', 'F. Ingreso', 'Almacén', 'Alm. Destino',
        'Entidad', 'OC', 'Documento', 'Total', 'Ítems', 'Notas',
      ];
      const guideHeaderRow = wsGuides.getRow(3);
      guideHeaders.forEach((h, i) => {
        const cell = guideHeaderRow.getCell(i + 1);
        cell.value = h;
        cell.style = headerStyle;
      });

      (reportData.guides_summary || []).forEach((g, idx) => {
        const row = wsGuides.addRow([
          g.code,
          g.guide_type_label,
          g.issue_date,
          g.entry_date,
          g.warehouse_name,
          g.destination_warehouse_name,
          g.supplier_name,
          g.purchase_order_code,
          g.document_number,
          g.total_amount,
          g.items_count,
          g.notes,
        ]);
        if (idx % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9F9F9' } };
          });
        }
        row.getCell(10).numFmt = '"S/"#,##0.0000';
      });

      wsGuides.columns = [
        { width: 16 }, { width: 14 }, { width: 12 }, { width: 12 },
        { width: 22 }, { width: 22 }, { width: 28 }, { width: 14 },
        { width: 14 }, { width: 14 }, { width: 8 }, { width: 30 },
      ];
      wsGuides.views = [{ state: 'frozen', ySplit: 3 }];

      // Hoja Detalle
      const wsDetail = workbook.addWorksheet('Detalle ítems');
      wsDetail.mergeCells('A1:Q1');
      wsDetail.getCell('A1').value = `Detalle ítems — ${reportData.period_label}`;
      wsDetail.getCell('A1').style = titleStyle;

      const detailHeaders = [
        'Código guía', 'Tipo', 'F. Ingreso', 'Almacén', 'Alm. Destino', 'Entidad',
        'SKU', 'Categoría', 'Producto', 'Cantidad', 'Unidad', 'P. Unit.', 'Total',
        'Evento kardex', 'Proyecto', 'Condición',
      ];
      const detailHeaderRow = wsDetail.getRow(3);
      detailHeaders.forEach((h, i) => {
        const cell = detailHeaderRow.getCell(i + 1);
        cell.value = h;
        cell.style = headerStyle;
      });

      (reportData.detail_rows || []).forEach((r, idx) => {
        const row = wsDetail.addRow([
          r.guide_code,
          r.guide_type_label,
          r.entry_date,
          r.warehouse_name,
          r.destination_warehouse_name,
          r.supplier_name,
          r.product_sku,
          r.category_name || '',
          r.product_name,
          r.quantity,
          r.unit_of_measure,
          r.unit_price,
          r.total,
          getKardexEventLabel(r),
          r.project_code,
          getConditionLabel(r),
        ]);
        if (idx % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9F9F9' } };
          });
        }
        row.getCell(10).numFmt = '#,##0.000';
        row.getCell(12).numFmt = '"S/"#,##0.0000';
        row.getCell(13).numFmt = '"S/"#,##0.0000';
      });

      wsDetail.columns = [
        { width: 16 }, { width: 14 }, { width: 12 }, { width: 20 }, { width: 20 },
        { width: 24 }, { width: 18 }, { width: 22 }, { width: 32 }, { width: 12 },
        { width: 10 }, { width: 12 }, { width: 12 }, { width: 16 }, { width: 12 },
        { width: 14 },
      ];
      wsDetail.views = [{ state: 'frozen', ySplit: 3 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Matriz_Entradas_Salidas_${monthNames[monthFrom - 1]}_${yearFrom}_a_${monthNames[monthTo - 1]}_${yearTo}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      setToast({ isOpen: true, message: 'Excel exportado correctamente', type: 'success' });
    } catch (error) {
      console.error('Error al exportar:', error);
      setToast({ isOpen: true, message: 'Error al exportar el reporte', type: 'error' });
    }
  };

  const detailRows = reportData?.detail_rows || [];
  const totalPages = Math.max(1, Math.ceil(detailRows.length / PAGE_SIZE));

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return detailRows.slice(start, start + PAGE_SIZE);
  }, [detailRows, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const isLimitedWithWarehouses = getLimitedUserScope(authService.getCurrentUser()).showAlmacenes;
  const allWarehousesLabel = isLimitedWithWarehouses ? 'Todos mis almacenes' : 'Todos los almacenes';

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Matriz Entradas y Salidas</h1>
        <p className="text-gray-600">
          Consolida todas las guías de almacén (entradas, salidas, traspasos, recepciones, ajustes e iniciales) en un solo reporte.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Desde:</span>
            <select
              value={monthFrom}
              onChange={(e) => setMonthFrom(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {monthNames.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              value={yearFrom}
              onChange={(e) => setYearFrom(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Hasta:</span>
            <select
              value={monthTo}
              onChange={(e) => setMonthTo(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {monthNames.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              value={yearTo}
              onChange={(e) => setYearTo(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Almacén:</label>
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {!isLimitedWithWarehouses || warehouses.length > 1 ? (
                <option value="">{allWarehousesLabel}</option>
              ) : null}
              {warehouses.map((wh) => (
                <option key={wh.id} value={wh.id}>{wh.code} - {wh.name}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={loadReport}
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Consultando...' : 'Consultar'}
          </button>

          {reportData && (
            <button
              type="button"
              onClick={exportToExcel}
              className="ml-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Exportar a Excel
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
          <p className="mt-2 text-gray-600">Cargando matriz...</p>
        </div>
      ) : !reportData ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">Seleccione el período y pulse Consultar</p>
        </div>
      ) : detailRows.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No hay guías en el período seleccionado</p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-4 text-sm text-gray-600">
            <span><strong>Período:</strong> {reportData.period_label}</span>
            <span><strong>Guías:</strong> {reportData.guides_count}</span>
            <span><strong>Ítems:</strong> {reportData.items_count}</span>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Código</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Tipo</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">F. Ingreso</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Almacén</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">SKU</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Categoría</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Producto</th>
                    <th className="px-3 py-3 text-right font-medium text-gray-700">Cant.</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Unid.</th>
                    <th className="px-3 py-3 text-right font-medium text-gray-700">Total</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Evento kardex</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Condición</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedRows.map((row, index) => (
                    <tr key={`${row.guide_id}-${(currentPage - 1) * PAGE_SIZE + index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-teal-700">{row.guide_code}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{row.guide_type_label}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.entry_date)}</td>
                      <td className="px-3 py-2">{row.warehouse_name}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{row.product_sku}</td>
                      <td className="px-3 py-2 text-gray-600">{row.category_name || '-'}</td>
                      <td className="px-3 py-2">{row.product_name}</td>
                      <td className="px-3 py-2 text-right">{Number(row.quantity).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                      <td className="px-3 py-2">{row.unit_of_measure}</td>
                      <td className="px-3 py-2 text-right">S/ {Number(row.total).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{getKardexEventLabel(row)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{getConditionLabel(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <span>
              Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, detailRows.length)} de {detailRows.length} ítems
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="px-2">
                Página {currentPage} de {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default EntriesExitsMatrix;
